import { EventContextProvider, useAnalytics } from "@codat/analytics";
import { FlaskIcon, SquareNewWindowIcon, useToast } from "@codat/orchard-ui";
import { datadogRum } from "@datadog/browser-rum";
import { useGetBillingPortalSession } from "api/queries/useGetBillingPortalSession";
import { BannerType, useBannerContext } from "contexts/BannerContext";
import { useProducts } from "contexts/ProductsContext";
import { differenceInDays } from "date-fns";
import useJwtProp from "hooks/useJwtProp";
import { useOnboarding } from "hooks/useOnboarding";
import usePageTitles from "hooks/usePageTitles";
import usePageViewEvents from "hooks/usePageViewEvents";
import { useFeatureStatus } from "portal-ui-remote/FeatureStatusContext";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { initDatadog } from "utils/datadog";
import { formatDateAndTimeWithShortString } from "utils/formatDate";

import { paths } from "components/App/paths";
import Banner from "components/Banner";
import Button from "components/Button";
import NavBar from "components/NavBar";
import { createNavPermissionMap } from "components/NavContainer/utils";
import NavHeader from "components/NavHeader";
import SigninMessageModal from "pages/Home/SigninMessageModal";

import { useGetSubscriptionStatus } from "../../api/portal";
import styles from "./NavContainer.module.css";

const NavContainer = ({ children }: React.PropsWithChildren) => {
  usePageViewEvents();
  usePageTitles();
  const { addToast } = useToast();
  const { featureStatus } = useFeatureStatus();
  const userFullName = useJwtProp("user_fullname");
  const { setUserId, setUserProperties } = useAnalytics();
  const history = useHistory();
  const { userState, error: userStateError } = useUserState();
  const { products, error: productsError } = useProducts();
  const [{ loading: billingSessionLoading }, getBillingSession] =
    useGetBillingPortalSession();
  const [{ data: subscriptionStatusResponse }, getSubscriptionStatus] =
    useGetSubscriptionStatus();
  const showOnboarding = useOnboarding();

  useEffect(() => {
    if (showOnboarding) {
      history.replace({
        pathname: paths.ONBOARDING,
        state: { prevPath: history.location.pathname },
      });
    }
  }, [showOnboarding, history]);

  const { banners, addBanner } = useBannerContext();

  const isLoading = !userState;

  const navPermissionMap = createNavPermissionMap(
    userState ? userState.userPermissions : [],
    userState?.userRole
  );

  useEffect(() => {
    const error = userStateError || productsError;

    const shouldShowErrorScreen =
      error && !userStateError?.isAuthError && !productsError?.isAuthError;

    if (shouldShowErrorScreen) {
      throw new Error(error?.response?.data?.error);
    }
  }, [userStateError, productsError]);

  useEffect(() => {
    if (userState) {
      const {
        id,
        clientName,
        clientType,
        userId,
        companyLimit,
        dataIntegrityEnabled,
        hourlySyncAllowed,
        freeAccount,
        isPartner,
        userPermissions,
        email,
        userRole,
      } = userState;

      const isCodatUser = email.includes("@codat.io");

      const userProperties = {
        clientId: id,
        clientName,
        clientType,
        userId,
        companyLimit,
        dataIntegrityEnabled,
        hourlySyncAllowed,
        freeAccount: !!freeAccount,
        isPartner,
        userPermissions,
        isCodatUser,
        userRole,
      };
      setUserId(userState.userId);
      setUserProperties(userProperties);
    }
  }, [setUserId, userState, setUserProperties]);
  useEffect(() => {
    if (!userState || !featureStatus) {
      return;
    }
    const isCodatUser = userState.email.includes("@codat.io");
    const isUserTrackingEnabled = featureStatus["PortalUI.UserTrackingEnabled"];

    if (isCodatUser || isUserTrackingEnabled) {
      initDatadog();
      datadogRum.setUser({
        id: userState.userId,
        clientId: userState.id,
      });
    }
  }, [featureStatus, userState]);

  useEffect(() => {
    if (products) {
      const enabledProducts = products.filter(
        (product) =>
          product.status === "Enabled" ||
          (product.status === "Unknown" && product.type === "Default")
      );

      const enabledProductIds = enabledProducts.map(
        (product) => product.productId
      );
      const enabledProductNames = enabledProducts.map(
        (product) => product.name
      );

      setUserProperties({
        clientProductsEnabled: enabledProductNames,
        clientProductIdsEnabled: enabledProductIds,
      });
    }
  }, [products, setUserProperties]);

  useEffect(() => {
    if (userState && userState.clientType === "ClientStartUp") {
      getSubscriptionStatus();
    }
  }, [userState, getSubscriptionStatus]);

  useEffect(() => {
    const hasMissedPayment =
      subscriptionStatusResponse &&
      ["PastDue", "Unpaid"].includes(
        subscriptionStatusResponse.subscriptionStatus
      );
    const userIsAdminOrDeveloper =
      userState &&
      (userState.userRole === "Administrator" ||
        userState.userRole === "Developer");

    if (hasMissedPayment) {
      const expiryDate = formatDateAndTimeWithShortString(
        userState?.expiry ?? ""
      );
      const daysToExpiry = differenceInDays(
        new Date(userState?.expiry ?? ""),
        new Date()
      );
      const variant = daysToExpiry <= 3 ? "error" : "warning";

      const handleGetPortalBilling = () => {
        getBillingSession()
          .then((res) => window.open(res.data, "_blank"))
          .catch(() => {
            addToast({
              message: "There was an error fetching your subscription",
              variant: "error",
            });
          });
      };

      addBanner({
        type: BannerType.MISSED_PAYMENT,
        index: 1,
        value: {
          title:
            "Your start-up plan payment failed, please update your payment details",
          subtitle: `Your account will expire on ${expiryDate}`,
          variant: variant,
          cta: userIsAdminOrDeveloper ? (
            <Button
              variant="primary"
              size="small"
              buttonName="Missed Payment Banner - Manage Subscription"
              data-testid="missed-payments-banner-cta"
              onClick={handleGetPortalBilling}
              isLoading={billingSessionLoading}
            >
              <span>Manage subscription</span>
              <SquareNewWindowIcon className={styles.missedPaymentCtaIcon} />
            </Button>
          ) : undefined,
        },
      });
    }
  }, [userState, subscriptionStatusResponse, getBillingSession, addToast]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const isTestClient =
      userState && userState.clientType == "ClientTestStartup";

    if (isTestClient) {
      addBanner({
        type: BannerType.TEST_CLIENT,
        index: 1,
        value: {
          title: "You are in your test client",
          icon: <FlaskIcon />,
          subtitle: `Configuration changes made here will not effect your production clients`,
          variant: "action",
        },
      });
    }
  }, [userState]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.container}>
      <>
        <div className={styles.skipLinkWrapper}>
          <a href="#page-content" className={styles.skipLink}>
            Skip to main content
          </a>
        </div>
        <EventContextProvider name="Nav Header">
          <NavHeader userState={userState} userFullName={userFullName} />
        </EventContextProvider>
        <div className={styles.bannerNavWrapper}>
          {banners.map((b) => (
            <EventContextProvider
              key={b.type}
              name={`${b.type.toString()} Banner`}
            >
              <Banner data-testid="banner" {...b.value}></Banner>
            </EventContextProvider>
          ))}
          <EventContextProvider name="Nav Bar">
            <NavBar isLoading={isLoading} navPermissionMap={navPermissionMap} />
          </EventContextProvider>
        </div>
      </>
      <main
        data-testid="page-content"
        id="page-content"
        className={styles.content}
      >
        {!isLoading && children}
      </main>
      <SigninMessageModal />
    </div>
  );
};

export default NavContainer;
